import React, { useState, useEffect } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import DataTable from "react-data-table-component";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import Select from "react-select";
import { customTableStyles } from "../../assets/CustomTableStyles";

function AssignFeePlan() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId = localStorage.getItem("centerId");

  const {
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
  } = useForm({
    defaultValues: {
      centerId: null,
      centerSessionId: null,
      feePlanId: null,
    },
  });

  const [feePlans, setFeePlans] = useState([]);
  const [allCentersData, setAllCentersData] = useState([]);
  const [centerSessions, setCenterSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [classesData, setClassesData] = useState([]);
  const [filteredSearchData, setFilteredSearchData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectAll, setSelectAll] = useState(false);

  const [centerClassesWithPlans, setCenterClassesWithPlans] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [selectedFeePlanDetails, setSelectedFeePlanDetails] = useState([]);

  const [searchTermPlans, setSearchTermPlans] = useState("");
  const [filteredPlansData, setFilteredPlansData] = useState([]);

  const feePlanId = useWatch({ control, name: "feePlanId" });
  const selectedCenter = useWatch({ control, name: "centerId" });
  const centerId = selectedCenter?.value;

  // ------------------ Fetch Fee Plans ------------------
  useEffect(() => {
    const fetchFeePlans = async () => {
      try {
        const response = await get(
          `FeePlan/GetAllFeePlanByOrganizationIdAsync?organizationId=${organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFeePlans(response);
      } catch (error) {
        console.error("Error fetching feePlans:", error);
      }
    };
    fetchFeePlans();
  }, [organizationId, token]);

  // ------------------ Fetch Centers ------------------
  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const centers = response || [];
        setAllCentersData(centers);

        if (
          savedCenterId &&
          savedCenterId !== "null" &&
          savedCenterId !== "0"
        ) {
          const selectedCenter = centers.find(
            (c) => c.id === parseInt(savedCenterId)
          );
          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
          }
        } else if (centers.length > 0) {
          // const firstCenter = { value: centers[0].id, label: centers[0].name };
          // setValue("centerId", firstCenter);
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };
    fetchCenters();
  }, [organizationId, token, setValue, savedCenterId]);

  // ------------------ Fetch Center Sessions ------------------
  useEffect(() => {
    const fetchCenterSessions = async () => {
      if (!centerId) {
        setCenterSessions([]);
        setValue("centerSessionId", null);
        setSelectedSessionId(null);
        return;
      }

      try {
        const response = await get(
          `FeePlan/GetCenterSessionsByCenterId?centerId=${centerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const sessions = response.centerSessions || [];
        setCenterSessions(sessions);

        // Auto-select first session
        if (sessions.length > 0) {
          const first = sessions[0];
          const option = {
            value: first.id,
            label: first.name || first.sessionName,
          };
          setValue("centerSessionId", option);
          setSelectedSessionId(first.id);
        } else {
          setValue("centerSessionId", null);
          setSelectedSessionId(null);
        }
      } catch (error) {
        console.error("Error fetching center sessions:", error);
        setCenterSessions([]);
        setValue("centerSessionId", null);
        setSelectedSessionId(null);
      }
    };

    fetchCenterSessions();
  }, [centerId, token, setValue]);

  // ------------------ Fetch Classes ------------------
  const fetchClassByCenterIdAndOrganizationId = async (
    selectedCenterId,
    selectedFeePlanId
  ) => {
    try {
      const response = await get(
        `FeePlan/GetCenterByOrganizationAndLevelsId?organizationId=${organizationId}&centerId=${selectedCenterId}&feePlanId=${selectedFeePlanId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const classData = (response.classes || []).map((cls) => ({
        id: cls.classId,
        className: cls.className,
        sessionName: cls.sessionName,
        feePlanName: cls.feePlanName,
        isSaudi: cls.isSaudi || false,
        feePlanId: cls.feePlanId,
        centerClassFeePlanId: cls.centerClassFeePlanId,
        status: cls.feePlanId ? 1 : 0, // Initial status from API
      }));

      setClassesData(classData);
      setFilteredSearchData(classData);
      setSelectAll(classData.every((item) => item.status === 1));
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClassesData([]);
    }
  };

  useEffect(() => {
    const selectedCenterId = centerId || 0;
    const selectedFeePlanId = feePlanId?.value || 0;

    if (selectedCenterId !== 0 && selectedFeePlanId !== 0) {
      fetchClassByCenterIdAndOrganizationId(
        selectedCenterId,
        selectedFeePlanId
      );
    } else {
      setClassesData([]);
      setFilteredSearchData([]);
      setSelectAll(false);
    }
  }, [centerId, feePlanId]);

  // ------------------ Handle Checkbox Change ------------------
  const handleCheckboxChange = (id) => {
    const updated = classesData.map((item) =>
      item.id === id ? { ...item, status: item.status === 1 ? 0 : 1 } : item
    );
    setClassesData(updated);
    setFilteredSearchData(updated);
    setSelectAll(updated.every((item) => item.status === 1));
  };

  // ------------------ Handle Select All ------------------
  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    const updated = classesData.map((item) => ({
      ...item,
      status: newSelectAll ? 1 : 0,
    }));
    setClassesData(updated);
    setFilteredSearchData(updated);
    setSelectAll(newSelectAll);
  };

  // ------------------ Search Filter ------------------
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredSearchData(classesData);
    } else {
      const lowerSearch = searchTerm.toLowerCase();
      const filtered = classesData.filter((item) =>
        item.className?.toLowerCase().includes(lowerSearch) ||
        item.sessionName?.toLowerCase().includes(lowerSearch) ||
        item.feePlanName?.toLowerCase().includes(lowerSearch)
      );
      setFilteredSearchData(filtered);
    }
  }, [searchTerm, classesData]);

  useEffect(() => {
    if (searchTermPlans.trim() === "") {
      setFilteredPlansData(centerClassesWithPlans);
    } else {
      const lowerSearch = searchTermPlans.toLowerCase();
      const filtered = centerClassesWithPlans.filter((item) =>
        item.centerClassName?.toLowerCase().includes(lowerSearch) ||
        item.feePlans?.toLowerCase().includes(lowerSearch)
      );
      setFilteredPlansData(filtered);
    }
  }, [searchTermPlans, centerClassesWithPlans]);

  // ------------------ Save ------------------
  const saveData = async (data) => {
    if (!feePlanId) return toast.error("Select a Fee Plan first!");
    if (!data.centerSessionId) {
      await trigger("centerSessionId");
      return;
    }

    // Use `status` to filter selected classes
    const selectedClasses = classesData.filter((row) => row.status === 1);

    // if (selectedClasses.length === 0) {
    //   toast.warn("No classes selected for assignment.");
    //   return;
    // }

    const payload = {
      centerId: centerId,
      feePlanId: feePlanId.value,
      centerSessionId: data.centerSessionId.value,
      createdBy: localStorage.getItem("userId"),
      feePlans: selectedClasses.map((row) => ({
        centerClassId: row.id,
      })),
    };

    // console.log(payload);
    // return;

    try {
      const response = await post(
        "FeePlan/AssignFeePlanCenterClassesAsync",
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.isSuccess) {
        fetchCenterClassesWithPlans();
        toast.success("Fee Plan assigned successfully!");
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Error saving Fee Plan assignment.");
    }
  };

  const fetchCenterClassesWithPlans = async () => {
    try {
      const response = await get(
        `FeePlan/GetCenterClassessWithFeePlans/${centerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCenterClassesWithPlans(response.classes || []);
    } catch (error) {
      console.error("Error fetching center classes with plans:", error);
    }
  };

  useEffect(() => {
    if (!centerId) return;
    fetchCenterClassesWithPlans();
  }, [centerId, token]);

  const plansColumns = [
    // {
    //   name: "Class ID",
    //   selector: (row) => row.centerClassId,
    //   sortable: true,
    // },
    {
      name: "Class",
      selector: (row) => row.centerClassName,
      sortable: true,
    },
    {
      name: "Fee Plan(s)",
      cell: (row) => {
        if (!row.feePlans) return "-";
        return row.feePlans.split(",").map((plan, index) => {
          let bgClass = "bg-info";
          if (index === 0) bgClass = "bg-success";
          else if (index === 1) bgClass = "bg-warning";
          return (
            <span
              key={index}
              className={`badge ${bgClass} me-1 cursor-pointer fs-10`}
              onClick={async () => {
                const planName = plan.trim();
                const feePlan = feePlans.find((f) => f.name === planName);
                if (!feePlan) return toast.error("Fee Plan not found.");
                const id = feePlan.id;
                try {
                  const response = await get(
                    `FeePlan/GetFeePlanByFeePlanId/${id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  if (response.isSuccess) {
                    setSelectedFeePlanDetails(response.classes || []);
                    setShowModal(true);
                  } else {
                    toast.error("Failed to fetch fee plan details.");
                  }
                } catch (error) {
                  console.error("Error fetching fee plan details:", error);
                  toast.error("Error fetching fee plan details.");
                }
              }}
            >
              {plan.trim()}
            </span>
          );
        });
      },
      sortable: true,
    },
  ];

  // ------------------ Columns WITH CHECKBOXES ------------------
  const columns = [
    {
      name: (
        <input
          type="checkbox"
          checked={feePlanId && selectAll}
          onChange={handleSelectAll}
          disabled={!feePlanId}
        />
      ),
      cell: (row) => (
        <input
          type="checkbox"
          checked={row.status === 1} // Use status to control checked state
          onChange={() => handleCheckboxChange(row.id)}
          disabled={!feePlanId}
        />
      ),
      width: "80px",
    },
    {
      name: "Class",
      selector: (row) => row.className || "-",
      sortable: true,
    },
    {
      name: "Session",
      selector: (row) => row.sessionName || "-",
      sortable: true,
    },
    // {
    //   name: "Is Saudi",
    //   selector: (row) => (
    //     <input
    //       type="checkbox"
    //       checked={row.isSaudi || false}
    //       disabled
    //       style={{ cursor: "not-allowed" }}
    //     />
    //   ),
    //   sortable: true,
    // },
    {
      name: "Fee Plan",
      selector: (row) => row.feePlanName || "-",
      sortable: true,
    },
  ];

  // ------------------ Render ------------------
  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Assign Fee Plan</h2>
      </div>
      <form className="new-form-style" onSubmit={handleSubmit(saveData)}>
        <div className="row g-3">
          {/* Left Panel */}
          <div className="col-md-7">
            <div className="card">
              <div className="card-body">
                <div className="row g-3">
                  {/* Center */}
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Center<span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="centerId"
                      control={control}
                      rules={{ required: "Center is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={field.value || null}
                          options={(allCentersData || []).map((center) => ({
                            value: center.id,
                            label: center.name,
                          }))}
                          onChange={(selectedOption) => {
                            setValue("centerId", selectedOption || null);
                            setValue("centerSessionId", null); // Reset session when center changes
                            trigger("centerId");
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          isClearable={
                            !savedCenterId ||
                            savedCenterId === "null" ||
                            savedCenterId === "0"
                          }
                          isDisabled={
                            savedCenterId &&
                            savedCenterId !== "null" &&
                            savedCenterId !== "0"
                          }
                          placeholder="Select Center"
                        />
                      )}
                    />
                    {errors.centerId && (
                      <small className="text-danger">
                        {errors.centerId.message}
                      </small>
                    )}
                  </div>

                  {/* Center Session */}
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Center Session<span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="centerSessionId"
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={field.value || null}
                          options={centerSessions.map((s) => ({
                            value: s.id,
                            label: s.name || s.sessionName,
                          }))}
                          onChange={(selectedOption) => {
                            field.onChange(selectedOption);
                            setSelectedSessionId(
                              selectedOption ? selectedOption.value : null
                            );
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          isClearable={false}
                          isDisabled={!centerId}
                        />
                      )}
                    />
                    {errors.centerSessionId && (
                      <small className="text-danger">
                        {errors.centerSessionId.message}
                      </small>
                    )}
                  </div>

                  {/* Fee Plan */}
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Fee Plan<span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="feePlanId"
                      control={control}
                      rules={{ required: "Fee Plan is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={(feePlans || []).map((f) => ({
                            value: f.id,
                            label: f.name,
                          }))}
                          onChange={(selectedOption) => {
                            setValue("feePlanId", selectedOption);
                            trigger("feePlanId");
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                        />
                      )}
                    />
                    {errors.feePlanId && (
                      <small className="text-danger">
                        {errors.feePlanId.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label form-label-new">Search</label>
                    <input
                      type="text"
                      placeholder="Search"
                      className="form-control w-100"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <div className="">
                    {classesData && classesData.length > 0 ? (
                      <>
                        {/* Search */}

                        {/* Table WITH CHECKBOXES */}
                        <DataTable
                          columns={columns}
                          data={filteredSearchData}
                          pagination
                          dense
                          customStyles={customTableStyles}
                          highlightOnHover
                        />

                        <div className="">
                          <button
                            className="btn btn-primary btn-sm"
                            type="submit"
                            disabled={
                              isSubmitting ||
                              !watch("feePlanId") ||
                              !watch("centerId")
                            }
                          >
                            {isSubmitting ? "Saving..." : "Save"}
                          </button>
                        </div>
                      </>
                    ) : (
                      feePlanId && (
                        <div className="col-12">
                          <small className="text-muted">
                            No classes found for selected center.
                          </small>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="col-md-5">
            <div className="card">
              <div className="card-body">
                {centerClassesWithPlans.length > 0 && (
                  <div className="">
                    <div className="d-flex">
                      <h5>Assigned Fee Plans</h5>
                      <div className="ms-auto">
                        <input
                          type="text"
                          placeholder="Search"
                          className="form-control py-1 px-2"
                          value={searchTermPlans}
                          onChange={(e) => setSearchTermPlans(e.target.value)}
                        />
                      </div>
                    </div>
                    <DataTable
                      columns={plansColumns}
                      data={filteredPlansData}
                      pagination
                      dense
                      customStyles={customTableStyles}
                      highlightOnHover
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>

      {showModal && (
        <>
          <div
            className="modal fade show"
            tabIndex="-1"
            style={{ display: "block" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="mb-0">Fee Plan Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <table className="table table-bordered align-middle text-center custom-table m-0">
                    <thead className="">
                      <tr>
                        <th className="text-start" style={{ width: "30%" }}>
                          Fee Type
                        </th>
                        <th style={{ width: "20%" }}>Fee Amount</th>
                        <th style={{ width: "15%" }}>VAT(%)</th>
                        <th style={{ width: "10%" }}>VAT Added Amount</th>
                        <th style={{ width: "50%" }}>Cycle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedFeePlanDetails?.map((fee, index) => (
                        <tr key={index}>
                          <td className="text-start">{fee.feeTypeName}</td>
                          <td>{fee.amount}</td>
                          <td>{fee.vat}</td>
                          <td>{fee.vatAddedAmount}</td>
                          <td className="text-center text-nowrap">
                            {fee.feeFrequency}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </>
  );
}

export default AssignFeePlan;
